'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { TERMINAL_AUDIO_BATCH_STATUSES } from '@kiao-voice/core'
import { resolveAudioExtensionFromFile } from '../batch-transcription/file-validation'
import { ANALYTICS_EVENTS, trackEvent } from '../../lib/analytics'
import { reportTrialUploadFailure } from '../../lib/error-reporting'
import { PROGRESS_POLL_MS } from '../batch-transcription/stage-progress'
import {
  AnonymousTrialApiError,
  fetchAnonymousResult,
  probeFileDurationSecs,
  submitAnonymousTrialSmart,
  type TrimmedUpload,
} from './api-client'
import {
  deriveTrialPanelView,
  initialTrialState,
  reduce,
  type TrialFailure,
  type TrialPanelView,
  type TrialState,
} from './trial-flow'
import { classifyTrialUploadFailure } from './upload-failure-reason'

/**
 * 上傳 → 排隊 → 轉錄 → 完成，全程留在這一頁。
 *
 * 所有會碰到外面世界的東西都是參數（`submit`／`fetchResult`／`probeDuration`／
 * `nowMs`／`pollMs`），測試注入替身就能把整條流程跑完，不必攔全域 `fetch`：
 * 攔全域的版本會連「我們有沒有真的每 2 秒問一次」都驗不到。
 *
 * 畫面在兩次輪詢之間也要動，所以時鐘（250ms）與輪詢（`pollMs`）是兩個節奏：
 * 進度的內插不等網路，慢一次的回應不會變成使用者讀成當掉的停格。
 */

/** 畫面時鐘的節奏。只推進內插，不打網路。 */
const CLOCK_TICK_MS = 250

const TERMINAL_STATUSES = new Set<string>(TERMINAL_AUDIO_BATCH_STATUSES)

export interface AnonymousTrialFlowOptions {
  submit?: typeof submitAnonymousTrialSmart
  fetchResult?: typeof fetchAnonymousResult
  probeDuration?: typeof probeFileDurationSecs
  trimToSecs?: typeof trimUploadToSecs
  nowMs?: () => number
  pollMs?: number
}

/**
 * 上傳前把前 N 秒切出來。
 *
 * 動態載入是重點：裁切器連著一個 MP4 解析器，而 `/transcription` 的首屏根本
 * 還不知道有沒有檔案。選了檔案、伺服器也說了要裁幾秒之後才把它抓下來。
 */
async function trimUploadToSecs(file: File, secs: number): Promise<TrimmedUpload> {
  const { trimFirstSeconds } = await import('./trim-first-minutes')
  const outcome = await trimFirstSeconds(file, secs)
  return {
    file: outcome.file,
    trimmed: outcome.trimmed,
    sourceDurationSecs: outcome.sourceDurationSecs,
  }
}

export interface AnonymousTrialFlow {
  state: TrialState
  view: TrialPanelView
  chooseFile: (file: File) => void
  /** 開檔案選擇器之前呼叫，記錄「有意願但還沒有檔案」的那一群人。 */
  notifyPickerOpened: (source: 'button' | 'dropzone') => void
  cancel: () => void
  reset: () => void
}

export function useAnonymousTrialFlow({
  submit = submitAnonymousTrialSmart,
  fetchResult = fetchAnonymousResult,
  probeDuration = probeFileDurationSecs,
  trimToSecs = trimUploadToSecs,
  nowMs = Date.now,
  pollMs = PROGRESS_POLL_MS,
}: AnonymousTrialFlowOptions = {}): AnonymousTrialFlow {
  const [state, dispatch] = useReducer(reduce, initialTrialState)
  const [now, setNow] = useState(() => nowMs())

  const abortRef = useRef<AbortController | null>(null)
  const submittingRef = useRef(false)
  const mountedRef = useRef(true)

  // 參數透過 ref 讀：呼叫端傳 inline 函式（常態）時不該重啟輪詢。
  const seams = useRef({ submit, fetchResult, probeDuration, trimToSecs, nowMs })
  seams.current = { submit, fetchResult, probeDuration, trimToSecs, nowMs }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
      abortRef.current = null
    }
  }, [])

  const shortId = state.kind === 'polling' ? state.shortId : null

  // 輪詢。只在 polling 狀態存在，拿到終局狀態就停——`done`／`failed` 會讓
  // `shortId` 變回 null，effect 自己收掉。
  useEffect(() => {
    if (shortId === null) return
    let cancelled = false
    let fetching = false

    const ask = async () => {
      if (fetching || cancelled) return
      fetching = true
      try {
        const result = await seams.current.fetchResult(shortId)
        if (cancelled || !mountedRef.current) return
        dispatch({ type: 'pollResult', result, nowMs: seams.current.nowMs() })
        if (TERMINAL_STATUSES.has(result.status)) cancelled = true
        // 輪詢拿到終局就停，所以同一次試轉只會送一次。
        if (result.status === 'completed') trackEvent(ANALYTICS_EVENTS.trialUploadCompleted)
      } catch (caughtError) {
        reportHandledException(caughtError, {
          source: 'features/anonymous-trial/use-anonymous-trial-flow.ts',
        })
        // 輪詢失敗不終止流程：結果頁仍在，下一次再問就好。把一次網路抖動
        // 變成「試轉失敗」會讓使用者以為檔案沒了。
      } finally {
        fetching = false
      }
    }

    void ask()
    const timer = setInterval(() => void ask(), pollMs)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [shortId, pollMs])

  // 畫面時鐘。只在真的會隨時間變化的狀態下跑。
  const ticking = state.kind === 'polling'
  useEffect(() => {
    if (!ticking) return
    const timer = setInterval(() => setNow(seams.current.nowMs()), CLOCK_TICK_MS)
    return () => clearInterval(timer)
  }, [ticking])

  const chooseFile = useCallback((file: File) => {
      if (submittingRef.current) return
      submittingRef.current = true
      // 在任何驗證之前發：「挑了檔案」與「挑了我們收的檔案」要分得開，
      // 兩者的差就是退件率。
      trackEvent(ANALYTICS_EVENTS.trialFileSelected)
      const meta = { name: file.name, sizeBytes: file.size }
      dispatch({ type: 'fileChosen', file: meta })

      const fail = (failure: TrialFailure) => {
        if (mountedRef.current) dispatch({ type: 'failed', failure })
      }

      void (async () => {
        const abortController = new AbortController()
        abortRef.current = abortController
        try {
          if (!resolveAudioExtensionFromFile(file)) {
            trackEvent(ANALYTICS_EVENTS.trialFileRejected, { reason: 'unsupported_format' })
            fail({ kind: 'unsupported_format' })
            return
          }
          // 只是為了把「12 分 30 秒」顯示在檔名旁邊。長度不再是拒絕的理由：
          // 任何長度都收，伺服器只轉前 `trimSecs` 秒。
          const duration = await seams.current.probeDuration(file)
          if (mountedRef.current && duration !== null) {
            dispatch({ type: 'fileChosen', file: { ...meta, durationSecs: duration } })
          }
          // 檔案過了預檢，訪客真的要上傳了。在請求之前發，失敗也算一次意圖。
          trackEvent(ANALYTICS_EVENTS.trialUploadStarted)
          const response = await seams.current.submit(file, {
            signal: abortController.signal,
            trimToSecs: (source, secs) => seams.current.trimToSecs(source, secs),
            onPhase: (phase) => {
              if (mountedRef.current) dispatch({ type: 'phase', phase })
            },
            onUploadProgress: (fraction) => {
              if (mountedRef.current) dispatch({ type: 'uploadProgress', fraction })
            },
          })
          if (mountedRef.current) dispatch({ type: 'submitted', shortId: response.shortId })
        } catch (cause) {
        reportHandledException(cause, {
          source: 'features/anonymous-trial/use-anonymous-trial-flow.ts',
        })
          if (cause instanceof DOMException && cause.name === 'AbortError') {
            if (mountedRef.current) dispatch({ type: 'cancelled' })
            return
          }
          fail(failureFromCause(cause))
        } finally {
          abortRef.current = null
          submittingRef.current = false
        }
      })()
  }, [])

  const notifyPickerOpened = useCallback((source: 'button' | 'dropzone') => {
    trackEvent(ANALYTICS_EVENTS.trialFilePickerOpened, { source })
  }, [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const reset = useCallback(() => {
    submittingRef.current = false
    dispatch({ type: 'reset' })
  }, [])

  const view = useMemo(() => deriveTrialPanelView(state, now), [state, now])

  return { state, view, chooseFile, notifyPickerOpened, cancel, reset }
}

/**
 * 把丟出來的東西變成一個失敗原因，並且**在這裡回報一次**——每一條分支都是
 * 「這次上傳掉了」的一種，分散在各分支回報過去漏掉過整整一類。
 *
 * 兩個去處是刻意的：埋點走我們自己的 API origin（也就是剛剛失敗的那條路），
 * 它送不出去的那些失敗，正好是 Sentry 存在的理由。
 */
function failureFromCause(cause: unknown): TrialFailure {
  const classified = classifyTrialUploadFailure(cause)
  if (classified) {
    trackEvent(ANALYTICS_EVENTS.trialUploadFailed, classified)
    reportTrialUploadFailure({ ...classified, cause })
  }

  if (cause instanceof AnonymousTrialApiError) {
    const extras = cause.extras
    if (cause.code === 'duration_unreadable') {
      trackEvent(ANALYTICS_EVENTS.trialFileRejected, { reason: 'duration_unreadable' })
      return { kind: 'duration_unreadable' }
    }
    if (cause.code === 'file_too_large') {
      trackEvent(ANALYTICS_EVENTS.trialFileRejected, { reason: 'file_too_large' })
      return { kind: 'file_too_large', maxBytes: positiveNumber(extras.maxBytes) }
    }
    if (cause.code === 'rate_limited') return { kind: 'rate_limited' }
    if (cause.code === 'anonymous_trial_disabled') return { kind: 'trial_disabled' }
    if (cause.code === 'upload_stalled') return { kind: 'upload_stalled' }
    if (cause.code === 'upload_not_found') return { kind: 'upload_not_found' }
  }

  return { kind: classified?.reason ?? 'other' }
}

function positiveNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
}
